import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  Search,
  RefreshCw,
  Copy,
  Database,
  Zap,
} from 'lucide-react';
import CommitmentService from '../../services/commitment';
import CustomFieldsService from '../../services/customFields/customFieldsService';
import { useAuth } from '../../contexts/AuthContext';
import { getSourceIcon } from './sourceTypeUtils';
import type { CommitmentSourceConfigFull, JiraField } from '../../shared/types';

export const CommitmentSourceConfigSection: React.FC = () => {
  const { currentProduct } = useAuth();
  const productName = currentProduct?.name?.trim() || null;

  const [configs, setConfigs] = useState<CommitmentSourceConfigFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [jiraFields, setJiraFields] = useState<JiraField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedField, setSelectedField] = useState<JiraField | null>(null);
  const [addMatchMode, setAddMatchMode] = useState<'exact' | 'json_contains'>('exact');
  const [saving, setSaving] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [fieldDropdownOpen, setFieldDropdownOpen] = useState(false);
  const [highlightConfigId, setHighlightConfigId] = useState<number | null>(null);
  const [showAdvancedMatchMode, setShowAdvancedMatchMode] = useState(false);
  const [matchModeAuto, setMatchModeAuto] = useState(true);
  // Per-row sync state. Mirrors ForecastCustomFieldsSection's contract so the
  // user gets the same SSE log / progress / outcome they see in Forecast.
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number } | null>(null);
  const [syncOutcome, setSyncOutcome] = useState<'ok' | 'partial' | 'error' | null>(null);
  const [syncLogs, setSyncLogs] = useState<string[]>([]);
  // Section-level "Apply to all products" state (same live-log feedback).
  const [applyingAll, setApplyingAll] = useState(false);

  const appendLog = useCallback((line: string) => {
    setSyncLogs((prev) => (prev.length > 240 ? [...prev.slice(-200), line] : [...prev, line]));
  }, []);

  /**
   * One-click fan-out of the ACTIVE product's source config (scope toggles,
   * delivery toggles, and custom source types) to every other product in the
   * org. Mirrors ForecastCustomFieldsSection.handleApplyToAll: window.confirm
   * with a summary of what will be overwritten, then live-log feedback.
   */
  const handleApplyToAll = async () => {
    const scopeOn = configs.filter((c) => !!c.enabled).length;
    const deliveryOn = configs.filter((c) => (c.deliveryEnabled ?? 0) === 1).length;
    const customCount = configs.filter((c) => c.storageMode === 'custom').length;
    if (
      !window.confirm(
        `Apply these commitment source field settings to EVERY product in the organization?\n\n` +
          `From "${productName ?? 'the active product'}":\n` +
          `• ${scopeOn} field(s) enabled for scope\n` +
          `• ${deliveryOn} field(s) enabled for delivery\n` +
          `• ${customCount} custom source type(s)\n\n` +
          `This overwrites the per-product source field settings of every other product.`
      )
    ) {
      return;
    }
    setApplyingAll(true);
    setSyncMessage('Applying source field settings to all products…');
    setSyncLogs([]);
    setSyncProgress(null);
    setSyncOutcome(null);
    appendLog(
      `Applying source field settings from "${productName ?? 'the active product'}" to every product…`
    );
    appendLog(
      `Scope-enabled: ${scopeOn}, delivery-enabled: ${deliveryOn}, custom types: ${customCount}`
    );
    try {
      const out = await CommitmentService.applySourceConfigsToAll();
      const msg = `Applied source field settings to ${out.appliedTo} product(s).`;
      appendLog(`✅ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('ok');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Apply-to-all failed';
      appendLog(`❌ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('error');
    } finally {
      setApplyingAll(false);
    }
  };

  const handleSyncField = async (cfg: CommitmentSourceConfigFull) => {
    const fieldId = cfg.jiraFieldId;
    if (!fieldId) {
      setError(`Cannot sync "${cfg.displayName}" — built-in fields refresh on Full Update only.`);
      return;
    }
    setSyncingId(cfg.id);
    setSyncMessage(`Syncing ${cfg.displayName}…`);
    setSyncLogs([]);
    setSyncProgress(null);
    setSyncOutcome(null);
    appendLog(
      `Sync now: pulling current Jira values for "${cfg.displayName}" (${fieldId}) for every task in this product…`
    );
    appendLog('Progress will update every batch. Do not close the page.');
    try {
      const out = await CustomFieldsService.syncFields([fieldId], (evt) => {
        if (evt.type === 'progress') {
          const current = evt.current ?? 0;
          const total = evt.total ?? 0;
          setSyncProgress({ current, total });
          if (evt.message) {
            appendLog(`▶ ${evt.message}`);
            setSyncMessage(evt.message);
          } else {
            const updatedPart = typeof evt.updated === 'number' ? `, ${evt.updated} touched` : '';
            const failedPart =
              typeof evt.failed === 'number' && evt.failed > 0 ? `, ${evt.failed} failed` : '';
            appendLog(`▶ ${current}/${total}${updatedPart}${failedPart}`);
          }
        }
      });
      const finalLine = out.message || `Done. ${out.updated}/${out.total} updated.`;
      const isPartial = out.failed > 0;
      appendLog(isPartial ? `⚠ ${finalLine}` : `✅ ${finalLine}`);
      setSyncMessage(finalLine);
      setSyncOutcome(isPartial ? 'partial' : 'ok');
      setSyncProgress({ current: out.total, total: out.total });
      // Refresh configs so the modal reads fresh values on next open.
      void fetchConfigs();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      appendLog(`❌ ${msg}`);
      setSyncMessage(msg);
      setSyncOutcome('error');
    } finally {
      setSyncingId(null);
    }
  };

  /**
   * Infer SQL match mode from a Jira field's `schema`. Multi-value Jira
   * fields (multi-select, labels, multi-version, …) store JSON arrays in
   * `issue_custom_fields.value_text`, single-value fields store scalars.
   * Picking the wrong mode silently returns zero matches in the Create
   * Commitment modal, so the user shouldn't have to guess — `/rest/api/3/field`
   * already tells us.
   */
  const inferMatchMode = (field: JiraField | null): 'exact' | 'json_contains' => {
    if (!field) return 'exact';
    const schema = field.schema as { type?: unknown; items?: unknown; system?: unknown } | null;
    const type = typeof schema?.type === 'string' ? schema.type : '';
    if (type === 'array') return 'json_contains';
    if (type === 'labels' || type === 'components') return 'json_contains';
    return 'exact';
  };

  /**
   * Re-key on the active product so a global product switch reloads the
   * per-product source-config rows. Without `currentProduct?.id` in the deps,
   * the table would keep showing the previous product's toggles.
   */
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CommitmentService.getSourceConfigs();
      setConfigs(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configs');
    } finally {
      setLoading(false);
    }
    // currentProduct?.id is in the deps on purpose: it changes the callback
    // identity so the useEffect below re-runs on product switch. The body
    // doesn't use it directly because apiFetch reads the X-Product-Id header
    // automatically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.id]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  /**
   * Close any half-finished Add form on product switch so we don't carry
   * over an in-progress field selection that may not be valid for the new
   * product (different role, different access).
   */
  useEffect(() => {
    setShowAddForm(false);
    setSelectedField(null);
    setFieldSearch('');
    setFieldDropdownOpen(false);
  }, [currentProduct?.id]);

  const handleToggleScope = async (cfg: CommitmentSourceConfigFull) => {
    try {
      const updated = await CommitmentService.updateSourceConfig(cfg.id, {
        enabled: !cfg.enabled,
      });
      setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle');
    }
  };

  const handleToggleDelivery = async (cfg: CommitmentSourceConfigFull) => {
    try {
      const on = !((cfg.deliveryEnabled ?? 0) === 1);
      const updated = await CommitmentService.updateSourceConfig(cfg.id, {
        deliveryEnabled: on,
      });
      setConfigs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle delivery');
    }
  };

  const handleDelete = async (cfg: CommitmentSourceConfigFull) => {
    if (
      !window.confirm(
        `Delete custom source type "${cfg.displayName}"? This will also remove all synced values for it.`
      )
    ) {
      return;
    }
    try {
      await CommitmentService.deleteSourceConfig(cfg.id);
      setConfigs((prev) => prev.filter((c) => c.id !== cfg.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleOpenAddForm = async () => {
    setShowAddForm(true);
    setSelectedField(null);
    setFieldSearch('');
    setAddMatchMode('exact');
    setShowAdvancedMatchMode(false);
    setMatchModeAuto(true);
    if (jiraFields.length === 0) {
      setLoadingFields(true);
      try {
        const fields = await CommitmentService.getJiraFields();
        setJiraFields(fields);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch Jira fields');
      } finally {
        setLoadingFields(false);
      }
    }
  };

  const handleAddConfig = async () => {
    if (!selectedField) return;
    if (!currentProduct?.id) {
      setError('No active product selected. Pick a product in the top bar first.');
      return;
    }
    setSaving(true);
    setError(null);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info('[CommitmentSourceConfigSection] addSourceConfig →', {
        productId: currentProduct?.id,
        productName: currentProduct?.name,
        displayName: selectedField.name,
        jiraFieldId: selectedField.id,
        matchMode: addMatchMode,
      });
    }
    try {
      const created = await CommitmentService.addSourceConfig({
        displayName: selectedField.name,
        jiraFieldId: selectedField.id,
        matchMode: addMatchMode,
        schema: selectedField.schema,
      });
      // Re-fetch the full list so we never show a stale or partial row, and
      // so storageMode / enabled flags exactly match what the server persisted.
      const fresh = await CommitmentService.getSourceConfigs();
      setConfigs(fresh);
      setShowAddForm(false);
      setSelectedField(null);
      setHighlightConfigId(created?.id ?? null);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info('[CommitmentSourceConfigSection] addSourceConfig OK', {
          createdId: created?.id,
          createdKey: created?.key,
          totalAfter: fresh.length,
          newRowVisible: fresh.some((c) => c.id === created?.id),
        });
      }
      window.setTimeout(() => {
        document
          .getElementById(`commitment-source-row-${created.id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
      window.setTimeout(() => setHighlightConfigId(null), 4000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add';
      setError(msg);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[CommitmentSourceConfigSection] addSourceConfig FAILED', err);
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredJiraFields = jiraFields.filter((f) => {
    const existingFieldIds = new Set(
      configs.filter((c) => c.jiraFieldId).map((c) => c.jiraFieldId)
    );
    if (existingFieldIds.has(f.id)) return false;
    if (!fieldSearch.trim()) return true;
    return (
      f.name.toLowerCase().includes(fieldSearch.toLowerCase()) ||
      f.id.toLowerCase().includes(fieldSearch.toLowerCase())
    );
  });

  const renderToggle = (cfg: CommitmentSourceConfigFull, on: boolean, onChange: () => void) => (
    <label
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 40,
        height: 22,
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: on ? 'var(--color-accent, #6366f1)' : 'var(--color-bg-elevated, #333)',
          borderRadius: 11,
          transition: 'background 0.2s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: on ? 20 : 2,
            top: 2,
            width: 18,
            height: 18,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 0.2s',
          }}
        />
      </span>
    </label>
  );

  const renderTitle = (
    <h2
      className="section-title"
      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}
    >
      <Settings className="label-icon" />
      <span>Commitment source fields</span>
      {productName ? (
        <span
          title="The toggles below apply to this product only. Switch product in the top bar to configure another."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 10px',
            borderRadius: 999,
            background: 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.4)',
            color: 'var(--color-accent, #6366f1)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          Active product: {productName}
        </span>
      ) : null}
    </h2>
  );

  if (loading) {
    return (
      <div className="card-section">
        {renderTitle}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
          <Loader2 className="animate-spin" size={16} />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="card-section">
      {renderTitle}
      <p className="section-description" style={{ marginBottom: 20 }}>
        Configure which Jira fields appear when you create a commitment for{' '}
        <strong>{productName ? `“${productName}”` : 'the active product'}</strong>; switch product
        in the top bar to configure another. <strong>Scope</strong> drives planning and baseline
        (Stayed / Added / Removed). <strong>Delivery</strong> drives whether an issue counts as
        shipped for delivery metrics. Built-in types use data already in the app; custom types sync
        from Jira on each Full Update.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          type="button"
          onClick={handleApplyToAll}
          disabled={applyingAll || syncingId !== null || !productName || configs.length === 0}
          title="Copy this product's scope/delivery toggles and custom source types to every product in the organization"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            border: '1px solid var(--color-border, #555)',
            background: 'transparent',
            color: 'var(--color-text-secondary, #aaa)',
            cursor:
              applyingAll || syncingId !== null || !productName || configs.length === 0
                ? 'not-allowed'
                : 'pointer',
            fontSize: 13,
            opacity:
              applyingAll || syncingId !== null || !productName || configs.length === 0 ? 0.6 : 1,
          }}
        >
          {applyingAll ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
          Apply to all products
        </button>
      </div>
      {!productName ? (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(245,158,11,0.12)',
            color: '#f59e0b',
            fontSize: 13,
            marginBottom: 12,
          }}
          role="status"
        >
          No product selected. Pick a product in the top bar to manage its per-product source fields
          below.
        </div>
      ) : null}

      {highlightConfigId !== null && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(34,197,94,0.15)',
            color: '#86efac',
            border: '1px solid rgba(34,197,94,0.4)',
            fontSize: 13,
            marginBottom: 12,
          }}
          role="status"
        >
          Custom source type added and enabled for scope. Run a <strong>Full Update</strong> in
          Releases / Forecast so its values sync from Jira before they appear in the Create
          Commitment modal.
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(239,68,68,0.15)',
            color: '#ef4444',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            dismiss
          </button>
        </div>
      )}

      <div id="settings-commitment-scope-types">
        <h3 style={{ margin: '24px 0 8px', fontSize: 16, fontWeight: 600 }}>Scope source types</h3>
        <p className="section-description" style={{ marginBottom: 12, fontSize: 13 }}>
          These Jira fields define the <strong>planning scope</strong> for commitments. When you
          create a commitment, you pick one of these fields and a value (for example Target Version
          = 3.26.04). The baseline and scope tracking (Stayed / Added / Removed) use this field.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border, #333)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Field</th>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Storage</th>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Column / Jira id</th>
              <th
                style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
                title={
                  productName
                    ? `Toggles whether this field can be picked as the planning scope when creating a commitment for "${productName}"`
                    : 'Per-product toggle'
                }
              >
                Use for scope
                {productName ? (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: 'var(--color-text-secondary, #888)',
                      marginTop: 2,
                    }}
                  >
                    for {productName}
                  </div>
                ) : null}
              </th>
              <th style={{ padding: '8px 4px', fontWeight: 500, width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {configs.map((cfg) => (
              <tr
                key={`scope-${cfg.id}`}
                id={`commitment-source-row-${cfg.id}`}
                className={
                  highlightConfigId === cfg.id ? 'commitment-source-row-highlight' : undefined
                }
                style={{ borderBottom: '1px solid var(--color-border, #333)' }}
              >
                <td style={{ padding: '10px 4px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {getSourceIcon(cfg.icon, 14)}
                    {cfg.displayName}
                  </span>
                </td>
                <td style={{ padding: '10px 4px' }}>
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background:
                        cfg.storageMode === 'builtin'
                          ? 'rgba(59,130,246,0.15)'
                          : 'rgba(168,85,247,0.15)',
                      color: cfg.storageMode === 'builtin' ? '#3b82f6' : '#a855f7',
                    }}
                  >
                    {cfg.storageMode}
                  </span>
                </td>
                <td
                  style={{
                    padding: '10px 4px',
                    fontSize: 12,
                    color: 'var(--color-text-secondary, #888)',
                  }}
                >
                  {cfg.jiraFieldId || cfg.dbColumn}
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  {renderToggle(cfg, !!cfg.enabled, () => handleToggleScope(cfg))}
                </td>
                <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                    }}
                  >
                    {cfg.storageMode === 'custom' && cfg.jiraFieldId ? (
                      <button
                        onClick={() => handleSyncField(cfg)}
                        disabled={syncingId !== null}
                        title={`Pull current Jira values for ${cfg.displayName} for every task in this product`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          background: 'transparent',
                          border: '1px solid var(--color-border, #555)',
                          color: 'var(--color-text-secondary, #aaa)',
                          cursor: syncingId !== null ? 'not-allowed' : 'pointer',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          opacity: syncingId !== null && syncingId !== cfg.id ? 0.5 : 1,
                        }}
                      >
                        {syncingId === cfg.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <RefreshCw size={12} />
                        )}
                        {syncingId === cfg.id ? 'Syncing…' : 'Sync now'}
                      </button>
                    ) : null}
                    {cfg.storageMode === 'custom' && (
                      <button
                        onClick={() => handleDelete(cfg)}
                        title="Delete custom source type"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: 4,
                          borderRadius: 4,
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div id="settings-commitment-delivery-types">
        <h3 style={{ margin: '28px 0 8px', fontSize: 16, fontWeight: 600 }}>
          Delivery source types
        </h3>
        <p className="section-description" style={{ marginBottom: 12, fontSize: 13 }}>
          These fields verify <strong>actual delivery</strong>. When you create a commitment, you
          can pick one of these (for example Fix Version) to decide if an issue counts as delivered.
          Delivery metrics (Delivered / Not Delivered) use this field instead of only checking
          done-status. Fix Version is enabled here by default.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border, #333)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Field</th>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Storage</th>
              <th style={{ padding: '8px 4px', fontWeight: 500 }}>Column / Jira id</th>
              <th
                style={{ padding: '8px 4px', fontWeight: 500, textAlign: 'center' }}
                title={
                  productName
                    ? `Toggles whether this field can verify actual delivery for commitments in "${productName}"`
                    : 'Per-product toggle'
                }
              >
                Use for delivery
                {productName ? (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: 'var(--color-text-secondary, #888)',
                      marginTop: 2,
                    }}
                  >
                    for {productName}
                  </div>
                ) : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {configs.map((cfg) => {
              const delOn = (cfg.deliveryEnabled ?? 0) === 1;
              return (
                <tr
                  key={`del-${cfg.id}`}
                  style={{ borderBottom: '1px solid var(--color-border, #333)' }}
                >
                  <td style={{ padding: '10px 4px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {getSourceIcon(cfg.icon, 14)}
                      {cfg.displayName}
                    </span>
                  </td>
                  <td style={{ padding: '10px 4px' }}>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background:
                          cfg.storageMode === 'builtin'
                            ? 'rgba(59,130,246,0.15)'
                            : 'rgba(168,85,247,0.15)',
                        color: cfg.storageMode === 'builtin' ? '#3b82f6' : '#a855f7',
                      }}
                    >
                      {cfg.storageMode}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '10px 4px',
                      fontSize: 12,
                      color: 'var(--color-text-secondary, #888)',
                    }}
                  >
                    {cfg.jiraFieldId || cfg.dbColumn}
                  </td>
                  <td style={{ padding: '10px 4px', textAlign: 'center' }}>
                    {renderToggle(cfg, delOn, () => handleToggleDelivery(cfg))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(syncMessage || syncProgress || syncLogs.length > 0) && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 8,
            border: `1px solid ${
              syncOutcome === 'error'
                ? 'rgba(239,68,68,0.4)'
                : syncOutcome === 'partial'
                  ? 'rgba(245,158,11,0.4)'
                  : 'var(--color-border, #444)'
            }`,
            background: 'var(--color-bg-elevated, #1e1e2e)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: syncLogs.length > 0 ? 8 : 0,
            }}
          >
            {syncingId !== null || applyingAll ? (
              <Loader2 className="animate-spin" size={14} />
            ) : syncOutcome === 'error' ? (
              <span aria-hidden style={{ color: '#ef4444' }}>
                ✕
              </span>
            ) : syncOutcome === 'partial' ? (
              <span aria-hidden style={{ color: '#f59e0b' }}>
                ⚠
              </span>
            ) : syncOutcome === 'ok' ? (
              <span aria-hidden style={{ color: '#22c55e' }}>
                ✓
              </span>
            ) : (
              <RefreshCw size={14} />
            )}
            <span style={{ fontSize: 13 }}>{syncMessage || 'Custom field sync'}</span>
            {syncProgress && syncProgress.total > 0 ? (
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary, #888)' }}>
                {syncProgress.current}/{syncProgress.total}
              </span>
            ) : null}
          </div>
          {syncLogs.length > 0 && (
            <pre
              style={{
                maxHeight: 160,
                overflow: 'auto',
                margin: 0,
                padding: 8,
                borderRadius: 6,
                background: 'var(--color-bg, #121212)',
                color: 'var(--color-text-secondary, #aaa)',
                fontSize: 11,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                whiteSpace: 'pre-wrap',
              }}
            >
              {syncLogs.join('\n')}
            </pre>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          padding: 12,
          borderRadius: 8,
          border: '1px dashed var(--color-border, #555)',
          background: 'rgba(99,102,241,0.04)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 280px', minWidth: 260 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 4,
                color: 'var(--color-text, #ddd)',
              }}
            >
              Three ways to populate values
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #888)' }}>
              <strong>Sync now</strong> (per row above) is fastest — surgically pulls values for one
              custom field. <strong>Full Update</strong> reloads every issue from Jira.{' '}
              <strong>Incremental Update</strong> only fetches issues changed since the last Full
              Update.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link
              to="/analytics#analytics-database-anchor"
              title="Open Analytics → Database Management to run a Full Update"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                background: 'var(--color-accent, #6366f1)',
                color: '#fff',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <Database size={14} />
              Full Update
            </Link>
            <Link
              to="/analytics#analytics-database-anchor"
              title="Open Analytics → Database Management to run an Incremental Update"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid var(--color-border, #555)',
                background: 'transparent',
                color: 'var(--color-text, #ddd)',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <Zap size={14} />
              Incremental Update
            </Link>
          </div>
        </div>
      </div>

      {!showAddForm ? (
        <button
          onClick={handleOpenAddForm}
          style={{
            marginTop: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            border: '1px dashed var(--color-border, #555)',
            background: 'transparent',
            color: 'var(--color-text-secondary, #aaa)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          <Plus size={14} />
          Add Custom Source Type
        </button>
      ) : (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 8,
            border: '1px solid var(--color-border, #444)',
            background: 'var(--color-bg-elevated, #1e1e2e)',
          }}
        >
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>
            Add Custom Source Type
          </h3>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}>
              Jira Field
            </label>
            {loadingFields ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  fontSize: 13,
                }}
              >
                <Loader2 className="animate-spin" size={14} />
                Loading Jira fields...
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setFieldDropdownOpen(!fieldDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid var(--color-border, #555)',
                    background: 'var(--color-bg, #121212)',
                    cursor: 'pointer',
                    fontSize: 13,
                    minHeight: 38,
                  }}
                >
                  {selectedField ? (
                    <span>
                      {selectedField.name}{' '}
                      <span style={{ color: 'var(--color-text-secondary, #888)', fontSize: 11 }}>
                        ({selectedField.id})
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>
                      Select a Jira field...
                    </span>
                  )}
                  <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
                </div>

                {fieldDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: 4,
                      borderRadius: 6,
                      border: '1px solid var(--color-border, #555)',
                      background: 'var(--color-bg, #121212)',
                      maxHeight: 240,
                      overflow: 'hidden',
                      zIndex: 100,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        padding: '8px 8px 4px',
                        borderBottom: '1px solid var(--color-border, #333)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 8px',
                          borderRadius: 4,
                          background: 'var(--color-bg-elevated, #1e1e2e)',
                        }}
                      >
                        <Search size={12} />
                        <input
                          type="text"
                          placeholder="Search fields..."
                          value={fieldSearch}
                          onChange={(e) => setFieldSearch(e.target.value)}
                          autoFocus
                          style={{
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            color: 'inherit',
                            fontSize: 12,
                            width: '100%',
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ overflowY: 'auto', maxHeight: 192 }}>
                      {filteredJiraFields.length === 0 ? (
                        <div
                          style={{
                            padding: '12px',
                            fontSize: 12,
                            color: 'var(--color-text-secondary, #888)',
                            textAlign: 'center',
                          }}
                        >
                          No matching fields found
                        </div>
                      ) : (
                        filteredJiraFields.map((f) => (
                          <div
                            key={f.id}
                            onClick={() => {
                              setSelectedField(f);
                              setFieldDropdownOpen(false);
                              setFieldSearch('');
                              if (matchModeAuto) {
                                setAddMatchMode(inferMatchMode(f));
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              fontSize: 13,
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--color-border, #222)',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLDivElement).style.background =
                                'var(--color-bg-elevated, #2a2a3e)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>{f.name}</span>
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: 11,
                                color: 'var(--color-text-secondary, #888)',
                              }}
                            >
                              {f.id}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            {selectedField ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  fontSize: 12,
                  color: 'var(--color-text-secondary, #aaa)',
                }}
              >
                <span>
                  Detected as{' '}
                  <strong style={{ color: 'var(--color-text, #ddd)' }}>
                    {addMatchMode === 'json_contains' ? 'multi-value (array)' : 'single value'}
                  </strong>{' '}
                  from Jira schema.
                </span>
                <button
                  type="button"
                  onClick={() => setShowAdvancedMatchMode((v) => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-accent, #6366f1)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 12,
                    textDecoration: 'underline',
                  }}
                >
                  {showAdvancedMatchMode ? 'Hide advanced' : 'Override (advanced)'}
                </button>
              </div>
            ) : null}

            {showAdvancedMatchMode ? (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}>
                  Match Mode
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: `1px solid ${addMatchMode === 'exact' ? 'var(--color-accent, #6366f1)' : 'var(--color-border, #555)'}`,
                      background: addMatchMode === 'exact' ? 'rgba(99,102,241,0.1)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="matchMode"
                      checked={addMatchMode === 'exact'}
                      onChange={() => {
                        setAddMatchMode('exact');
                        setMatchModeAuto(false);
                      }}
                      style={{ display: 'none' }}
                    />
                    Single value
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: `1px solid ${addMatchMode === 'json_contains' ? 'var(--color-accent, #6366f1)' : 'var(--color-border, #555)'}`,
                      background:
                        addMatchMode === 'json_contains' ? 'rgba(99,102,241,0.1)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="matchMode"
                      checked={addMatchMode === 'json_contains'}
                      onChange={() => {
                        setAddMatchMode('json_contains');
                        setMatchModeAuto(false);
                      }}
                      style={{ display: 'none' }}
                    />
                    Multi-value (array)
                  </label>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--color-text-secondary, #888)',
                    marginTop: 6,
                    display: 'block',
                  }}
                >
                  Only override if the auto-detected mode returns no values in the Create Commitment
                  modal — that means Jira stores this field differently than its schema declares.
                </span>
              </div>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleAddConfig}
              disabled={!selectedField || saving}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: !selectedField ? '#555' : 'var(--color-accent, #6366f1)',
                color: '#fff',
                cursor: !selectedField ? 'not-allowed' : 'pointer',
                fontSize: 13,
              }}
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Add
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setSelectedField(null);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid var(--color-border, #555)',
                background: 'transparent',
                color: 'var(--color-text-secondary, #aaa)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
